import { QrCode, Smartphone, Star, BarChart3, ArrowRight, Heart } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const Approach = () => {
  const { language } = useLanguage();
  
  const steps = [
    {
      icon: QrCode,
      title: language === "fr" ? "Scan" : "Scan",
      description: language === "fr" 
        ? "Le client scanne le QR code affiché dans votre commerce"
        : "Customer scans the QR code displayed in your business",
      number: "01",
      detail: language === "fr" ? "5 sec" : "5 sec",
    },
    {
      icon: Smartphone,
      title: language === "fr" ? "Soutenir" : "Support",
      description: language === "fr"
        ? "Un seul bouton : « ❤️ Soutenir le lieu » — zéro réflexion"
        : "One button: '❤️ Support this place' — zero thinking",
      number: "02",
      detail: language === "fr" ? "1 tap" : "1 tap",
    },
    {
      icon: Star,
      title: language === "fr" ? "Action" : "Action",
      description: language === "fr"
        ? "Ouverture directe d'Instagram ou Google — le client agit sur la vraie plateforme"
        : "Direct opening of Instagram or Google — customer acts on the real platform",
      number: "03",
      detail: language === "fr" ? "10 sec" : "10 sec",
    },
    {
      icon: BarChart3,
      title: language === "fr" ? "Mesure" : "Measure",
      description: language === "fr"
        ? "Chaque interaction est tracée. Vous voyez ce qui marche."
        : "Every interaction is tracked. You see what works.",
      number: "04",
      detail: language === "fr" ? "temps réel" : "real-time",
    },
  ];

  return (
    <section id="approach" className="py-12 lg:py-16 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 mb-3">
            <span className="text-xs font-medium text-emerald-600">
              {language === "fr" ? "⚡ Tunnel complet en moins de 30 secondes" : "⚡ Complete tunnel in under 30 seconds"}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-slate-800 mb-2">
            {language === "fr" ? "On supprime toutes les frictions" : "We remove all friction"}
          </h2>
          <p className="text-slate-500 max-w-lg mx-auto text-sm">
            {language === "fr" 
              ? "Parce que chercher le lieu, se connecter, écrire... c'est pénible. On simplifie tout."
              : "Because searching for the place, logging in, writing... it's tedious. We simplify everything."}
          </p>
        </div>

        {/* Timeline horizontal */}
        <div className="relative">
          {/* Ligne de connexion en pointillés */}
          <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 border-t-2 border-dashed border-slate-300" />
          
          {/* Shooting star animation */}
          <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 overflow-visible">
            <div className="shooting-star absolute flex items-center">
              <Heart className="w-3 h-3 text-rose-400 fill-rose-300 drop-shadow-[0_0_6px_rgba(244,63,94,1)]" />
              {/* Particles */}
              <span className="particle particle-1"></span>
              <span className="particle particle-2"></span>
              <span className="particle particle-3"></span>
              <span className="particle particle-4"></span>
              <span className="particle particle-5"></span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative flex flex-col items-center text-center group">
                {/* Numéro avec cercle */}
                <div className={`relative z-10 w-16 h-16 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center mb-4 shadow-lg group-hover:border-rose-400 group-hover:shadow-rose-200/50 transition-all duration-300 approach-number-${index + 1}`}>
                  <span className={`text-xl font-display font-bold bg-gradient-to-br from-rose-500 to-pink-600 bg-clip-text text-transparent approach-number-text-${index + 1}`}>
                    {step.number}
                  </span>
                </div>
                
                {/* Icône */}
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-3 group-hover:bg-rose-50 transition-colors">
                  <step.icon className="h-5 w-5 text-slate-500 group-hover:text-rose-600 transition-colors" />
                </div>
                
                <h3 className="text-base font-display font-semibold text-slate-800 mb-1 group-hover:text-rose-600 transition-colors">
                  {step.title}
                </h3>
                {/* Time indicator */}
                <span className="text-xs font-medium text-rose-400 mb-2">
                  {step.detail}
                </span>
                <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
                  {step.description}
                </p>
                
                {/* Flèche vers le suivant (mobile) */}
                {index < steps.length - 1 && (
                  <ArrowRight className="md:hidden w-5 h-5 text-slate-300 mt-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Approach;
